import { inject, Injectable } from '@angular/core';
import { ApiService } from './api.service';
import {
  AlarmIDS,
  AlarmIDSDisabled,
  GetAlarmsCenterMapViewResponse,
  GetAlarmsCountParams,
  GetAlarmsCountResponse,
  GetAlarmsDeviceParams,
  GetAlarmsMapViewParams,
  GetAlarmsMapViewResponse,
  GetAlarmsParams,
  GetAlarmsResponse,
} from '../models/alarms.models';
import { HttpParams } from '@angular/common/http';
import {
  ERROR_CONTEXT_ALARMS,
  ERROR_CONTEXT_HEADER,
  GET_ALARMS_CENTER_MAP_VIEW_ENDPOINT,
  GET_ALARMS_COUNT_ENDPOINT,
  GET_ALARMS_DISABLED_ENDPOINT,
  GET_ALARMS_ENDPOINT,
  GET_ALARMS_MAP_VIEW_ENDPOINT,
} from '../constants/endpoints.constants';
import { of } from 'rxjs';
import { ErrorHandlerService } from '@errorHandler';
import { GET_DEVICE_ALARMS_DATA } from '../constants/mocked-data.constants';
import { Filter } from '@data';
import { withSpinner } from '../../app/core/services/spinner.controller';

@Injectable({ providedIn: 'root' })
export class DataAlarmsService {
  protected readonly apiService = inject(ApiService);
  protected readonly errorService = inject(ErrorHandlerService);

  getAlarms(params: GetAlarmsParams) {
    const {
      dateFrom,
      dateTo,
      active,
      filters,
      centerId,
      priority,
      query,
      orderBy,
      order,
      pageIndex,
      pageSize,
    } = params;
    const queryObj: Record<
      string,
      string | number | boolean | readonly (string | number | boolean)[]
    > = {};
    if (dateFrom !== null && dateFrom !== undefined)
      queryObj['dateFrom'] = dateFrom;
    if (dateTo !== null && dateTo !== undefined) queryObj['dateTo'] = dateTo;
    if (active !== null && active !== undefined) queryObj['active'] = active;
    // if (centerId !== null && centerId !== undefined) queryObj['centerId'] = centerId;
    if (priority !== null && priority !== undefined)
      queryObj['priority'] = priority;
    if (orderBy !== null && orderBy !== undefined)
      queryObj['orderBy'] = orderBy;
    if (order !== null && order !== undefined) queryObj['order'] = order;
    if (pageSize !== null && pageSize !== undefined)
      queryObj['pageSize'] = pageSize;
    if (pageIndex !== null && pageIndex !== undefined)
      queryObj['pageIndex'] = pageIndex;
    const queryParams = new HttpParams().appendAll(queryObj);
    const body: any = this.getFiltersBody(query, filters);
    return this.apiService
      .post<GetAlarmsResponse>(GET_ALARMS_ENDPOINT, body, {
        params: queryParams,
      })
      .pipe(withSpinner());
    // return of(GET_ALARMS_DATA)
  }

  /**
   * Fetches the alarms count with the given parameters.
   * @param params Optional parameters for filtering the alarms count.
   * @returns An observable containing the alarms count response.
   */
  getAlarmsCount(params?: GetAlarmsCountParams) {
    params = { type: 'status', ...params };
    const queryParams = new HttpParams().appendAll({ ...params });
    return this.apiService.get<GetAlarmsCountResponse>(
      GET_ALARMS_COUNT_ENDPOINT,
      queryParams,
    );
  }

  getDeviceAlarms(params?: GetAlarmsDeviceParams) {
    // TODO: Connect to the endpoint
    return of(GET_DEVICE_ALARMS_DATA);
  }

  getCategoryAlarms(params: GetAlarmsParams) {
    const { dateFrom, active } = params;
    const queryObj: Record<
      string,
      string | number | boolean | readonly (string | number | boolean)[]
    > = {};
    if (dateFrom !== null && dateFrom !== undefined)
      queryObj['dateFrom'] = dateFrom;
    if (active !== null && active !== undefined) queryObj['active'] = active;
    const queryParams = new HttpParams().appendAll(queryObj);
    const body: any = this.getFiltersBody('', []);
    return this.apiService
      .post<GetAlarmsResponse>(GET_ALARMS_ENDPOINT, body, {
        params: queryParams,
      })
      .pipe(withSpinner());
    // return of(GET_CATEGORY_ALARMS_DATA);
  }

  disabledAlarm(alarmIds?: AlarmIDS) {
    const queryObj: Record<
      string,
      string | number | boolean | readonly (string | number | boolean)[]
    > = {};
    const queryParams = new HttpParams().appendAll(queryObj);
    return this.apiService
      .post<AlarmIDSDisabled>(GET_ALARMS_DISABLED_ENDPOINT, alarmIds, {
        params: queryParams,
        headers: {
          [ERROR_CONTEXT_HEADER]: ERROR_CONTEXT_ALARMS,
        },
      })
      .pipe(withSpinner());
  }

  /**
   * Fetches alarms data for map view with the given parameters.
   * This endpoint provides alarm information with geographical coordinates for map visualization.
   * Note: distributorIds are automatically added by the Tenants interceptor.
   * @param params Parameters for filtering the alarms map view including date range and filters.
   * @returns An observable containing the alarms map view response with geographical data.
   */
  getAlarmsMapView(params: GetAlarmsMapViewParams) {
    const { dateFrom, dateTo, active, centerId, priority, filters, query } =
      params;
    const queryObj: Record<
      string,
      string | number | boolean | readonly (string | number | boolean)[]
    > = {};

    if (dateFrom !== null && dateFrom !== undefined)
      queryObj['dateFrom'] = dateFrom;
    if (dateTo !== null && dateTo !== undefined) queryObj['dateTo'] = dateTo;
    if (active !== null && active !== undefined) queryObj['active'] = active;
    if (centerId !== null && centerId !== undefined)
      queryObj['centerId'] = centerId;
    if (priority !== null && priority !== undefined)
      queryObj['priority'] = priority;

    const queryParams = new HttpParams().appendAll(queryObj);
    const body: any = this.getFiltersBody(query, filters);

    return this.apiService
      .post<GetAlarmsMapViewResponse>(GET_ALARMS_MAP_VIEW_ENDPOINT, body, {
        params: queryParams,
      })
      .pipe(withSpinner());
  }

  /**
   * Retrieves detailed alarm data for a specific center, including transformer and line structure.
   * This endpoint is called when clicking on a map marker to get center-specific alarm details.
   * Uses POST method with the same filter structure as the alarms table API.
   *
   * @param params - Parameters including centerId, and the same filter params as getAlarms
   * @returns An Observable containing the center alarm structure with transformers, panels, and lines
   */
  getAlarmsCenterMapView(params: GetAlarmsParams & { centerId: string }) {
    const { centerId, dateFrom, dateTo, active, filters, query } = params;
    const queryObj: Record<
      string,
      string | number | boolean | readonly (string | number | boolean)[]
    > = {};

    if (dateFrom !== null && dateFrom !== undefined)
      queryObj['dateFrom'] = dateFrom;
    if (dateTo !== null && dateTo !== undefined) queryObj['dateTo'] = dateTo;
    if (active !== null && active !== undefined) queryObj['active'] = active;

    const queryParams = new HttpParams().appendAll(queryObj);

    // Use the same filter body structure as getAlarms
    const body: any = this.getFiltersBody(query, filters);

    // Replace centerId placeholder in endpoint URL
    const endpoint = GET_ALARMS_CENTER_MAP_VIEW_ENDPOINT.replace(
      '{{centerId}}',
      centerId,
    );

    return this.apiService
      .post<GetAlarmsCenterMapViewResponse>(endpoint, body, {
        params: queryParams,
      })
      .pipe(withSpinner());
  }

  private getFiltersBody(
    query: string | undefined,
    filters: Filter[] | undefined,
  ) {
    if (filters !== null && filters !== undefined) {
      let bodyFilters = filters!.map((filter) => ({
        ...filter,
        operator: filter?.operator || 'equals',
      }));
      bodyFilters = [...bodyFilters];
      const body: any = { filters: bodyFilters, query };
      return body;
    }
  }
}
